/** @jsx jsx */

import React, { useState, useMemo } from "react";

import { jsx, SerializedStyles, css } from "@emotion/core";
import { Typography, Upload, Button, Icon, Tag, Row, Col, Modal } from "antd";
import { ErrorMessage, Field, FieldProps } from "formik";

import { RcFile } from "antd/lib/upload";

import { formHelperStyles, ModalInputTagsStyles } from "./FormHelperStyles";

import { formHelperLabels as LABELS } from "./FormHelperLabels";
import { validateFile } from "../../helper/FileValidator";
import ApiService from "../../services/ApiService";
import { ServiceType } from "../../services/ServiceType.data";
import { QueryPath } from "../../services/QueryPath.data";
import { FileConfig, DEFAULT_FILE_CONFIG } from "../../Constants";
const { Text } = Typography;

interface Props {
  name: string;
  fileList: FileObject[];
  /**
   * by default pdf files are accepted
   * eg: [".pdf"]
   */
  acceptableFileExtensions?: string[];

  /**
   * Maximum fileSize in bytes
   */
  maximumFileSize?: number;
  /**
   * by default only single file is accepted
   */
  multiple?: boolean;
  /**
   *  by default "name" is used to indicate the field name in UI
   *  but if a custom label is to be shown this overrides "name"
   */
  label?: string;
  mandatory?: boolean;
  disableForm?: boolean;
  disableUpload?: boolean;
  hideError?: boolean;
  customError?: string;
  /**
   * customStyles provided overrides default styles using
   * "Composition" behavior of emotion.
   * reference: https://emotion.sh/docs/composition
   */
  customStyles?: SerializedStyles;

  className?: string;

  /**
   * configuration values for file upload and delete services
   */
  config: {
    uploadServiceType: ServiceType;
    uploadPath: QueryPath;
    /**
     * use the uploadServiceType if deleteServiceType is not defined explicitly
     */
    deleteServiceType?: ServiceType;
    /**
     * use the uploadPath if deletePath is not defined explicitly
     */
    deletePath?: QueryPath;
    uploadParams?: object;
  };
  /**
   * callback to perform any action on fileupload like select field clear
   */
  onAdd?: () => void;
  /**
   * If true, hide all the files of every subDocType except the latest uploaded one.
   */
  hideStaleSubDocTypeFiles?: boolean;
}

export interface FileObject {
  id: string | number;
  name: string;
  status: FileStatus;
  removed: boolean;
  error?: string;
  subDocType?: string;

  //to include any other extra keys necessary
  [key: string]: any;
}

export enum FileStatus {
  Success = "Uploaded Successfully",
  Failed = "Upload Failed",
  Invalid = "File type/size is invalid",
  Processing = "Upload In-progress"
}

/**
 *
 * @param {Props} props
 * @returns a reusable, customizable file upload component to be used only within context of formik.
 */
const FormFileUpload: React.FC<Props> = (props: Props) => {
  const {
    name,
    fileList,
    acceptableFileExtensions,
    maximumFileSize,
    multiple,
    label,
    mandatory,
    disableForm,
    disableUpload,
    hideError,
    customError,
    className,
    customStyles,
    onAdd,
    config,
    hideStaleSubDocTypeFiles
  } = props;

  const accept: string = useMemo(
    () =>
      acceptableFileExtensions ? acceptableFileExtensions.join(",") : ".pdf",
    [acceptableFileExtensions]
  );

  const fileConfig: FileConfig = useMemo(
    () => ({
      ...DEFAULT_FILE_CONFIG,
      acceptableFileExtensions:
        acceptableFileExtensions ||
        DEFAULT_FILE_CONFIG.acceptableFileExtensions,
      maximumFileSize: maximumFileSize || DEFAULT_FILE_CONFIG.maximumFileSize
    }),
    [acceptableFileExtensions, maximumFileSize]
  );

  const [open, setOpen] = useState(false);
  const handleCancel = () => setOpen(false);
  const showMore = () => {
    setOpen(true);
  };

  /**
   * to get the "key" which is to be updated by formik
   */
  const getPath = (index: number, key?: string) =>
    `${name}[${index}]${key ? `.${key}` : ""}`;
  return (
    <section className={className} css={[formHelperStyles, customStyles]}>
      <Row className="mt-3">
        <Field name={name}>
          {({
            field,
            form: { setFieldValue, setFieldTouched }
          }: FieldProps) => {
            const handleAdd = async (file: RcFile) => {
              setFieldTouched(name);
              const currentIndex = fileList.length;
              const fileInvalid = validateFile(file, fileConfig);

              try {
                fileList.push({
                  name: file.name,
                  status: fileInvalid
                    ? FileStatus.Invalid
                    : FileStatus.Processing,
                  removed: false,
                  id: file.uid,
                  subDocType: config?.uploadParams
                    ? config?.uploadParams[`subDocType`]
                    : ""
                });

                if (onAdd) {
                  onAdd();
                }

                setFieldTouched(getPath(currentIndex, "value"));

                //call handler
                if (fileInvalid) {
                  setFieldValue(getPath(currentIndex, "error"), fileInvalid);
                } else {
                  const uploadStatus = await upload(file);
                  if (uploadStatus) {
                    const index = fileList.findIndex(
                      (obj: FileObject) => obj.id === file.uid
                    );
                    setFieldValue(getPath(index, "status"), FileStatus.Success);
                    setFieldValue(getPath(index, "id"), uploadStatus.id);
                    if (hideStaleSubDocTypeFiles) {
                      const {
                        subDocType: subDocTypeToCheck,
                        id: idToCheck
                      } = fileList[index];
                      if (subDocTypeToCheck && idToCheck) {
                        let staleFileIndexes: number[] = [];
                        fileList.forEach(({ subDocType, id }, i) => {
                          if (
                            subDocType === subDocTypeToCheck &&
                            id !== idToCheck
                          ) {
                            staleFileIndexes = [...staleFileIndexes, i];
                          }
                        });
                        staleFileIndexes.forEach((staleFileIndex: number) => {
                          setFieldValue(
                            getPath(staleFileIndex, "removed"),
                            true
                          );
                        });
                      }
                    }
                  }
                  setFieldValue(getPath(currentIndex, "error"), undefined);
                }
              } catch (error) {
                setFieldValue(
                  getPath(currentIndex, "error"),
                  error.message || error
                );
                setFieldValue(
                  getPath(currentIndex, "status"),
                  FileStatus.Failed
                );
              }
            };

            const upload = (file: RcFile): Promise<any> => {
              return new ApiService(
                config.uploadServiceType,
                true
              ).postMultipart(config.uploadPath, {
                file,
                ...config.uploadParams
              });
            };

            const remove = (fileId: string | number): Promise<boolean> => {
              return new ApiService(
                config.deleteServiceType
                  ? config.deleteServiceType
                  : config.uploadServiceType,
                true //To prevent Application level error handling
              ).delete({
                route: config.deletePath
                  ? config.deletePath.route
                  : config.uploadPath.route,
                query: {
                  docId: fileId
                }
              });
            };
            const handleClose = async (file: FileObject) => {
              if (file.status !== FileStatus.Processing) {
                const index = fileList.findIndex(
                  (obj: FileObject) => obj.id === file.id
                );
                try {
                  if (file.status === FileStatus.Success) {
                    await remove(file.id);
                  }
                  setFieldValue(getPath(index, "removed"), true);
                } catch (error) {
                  setFieldValue(
                    getPath(index, "error"),
                    error.message || error
                  );
                  setFieldValue(getPath(index, "removed"), false);
                }
              }
            };

            const inputTags = (files: FileObject[], isModal: boolean) => {
              return files
                .filter(file => !file.removed)
                .map((file: FileObject, index: number) => (
                  <Col key={index} span={isModal ? 8 : 10}>
                    <Tag
                      visible={!file.removed}
                      closable={
                        file.status !== FileStatus.Processing && !disableForm
                      }
                      onClose={() => {
                        handleClose(file);
                      }}
                      className={`px-2 mr-1 mb-1 d-flex align-items-center ant-tag-ellipse ${
                        file.status === FileStatus.Success
                          ? "ant-tag-green"
                          : file.status === FileStatus.Processing
                          ? "ant-tag-grey"
                          : "ant-tag-red"
                      }`}
                    >
                      <Text className="ant-tag-name-ellipsis">
                        {file.subDocType}
                        {file.subDocType && " - "}
                        {file.name}
                      </Text>
                      {file.status === FileStatus.Processing && (
                        <Icon type="loading" />
                      )}
                    </Tag>
                    {file.error && !file.removed && (
                      <span className={`error ${isModal ? "pl-3" : "pl-1"}`}>
                        {file.error}
                      </span>
                    )}
                  </Col>
                ));
            };

            const renderModal = (
              <Modal
                visible={open}
                title={FormMultiInputTagsLabels.modal.title}
                footer={null}
                onCancel={handleCancel}
                css={ModalInputTagsStyles}
                centered
              >
                <Row type="flex" className="flex-grow-1" gutter={8}>
                  {inputTags(fileList, true)}
                </Row>
              </Modal>
            );

            return (
              <React.Fragment>
                <div
                  css={FileUploadStyles}
                  className="d-flex justify-content-between"
                >
                  <div>
                    <label>
                      {label}
                      <Text className="mandatory">
                        {mandatory && LABELS.mandatory}
                      </Text>
                    </label>
                  </div>
                  <div className="d-flex flex-column">
                    <Upload
                      name={field.name}
                      disabled={disableUpload || disableForm}
                      accept={accept}
                      multiple={multiple}
                      beforeUpload={(file: RcFile) => {
                        handleAdd(file);
                        return false;
                      }}
                      showUploadList={false}
                    >
                      <Button
                        disabled={disableUpload || disableForm}
                        type="link"
                        className="p-0"
                      >
                        <Icon type="upload" />
                      </Button>
                    </Upload>
                    {!hideError && (
                      <ErrorMessage name={name}>
                        {message => {
                          const errorMessage = customError || message; //To make sure only 1 error message is shown.
                          const renderMessage =
                            typeof errorMessage === "string"
                              ? errorMessage
                              : "";
                          return <Text className="error">{renderMessage}</Text>;
                        }}
                      </ErrorMessage>
                    )}
                  </div>
                </div>
                {fileList?.length > 0 && (
                  <Col span={24} className="mt-1">
                    <Row gutter={8}>
                      {inputTags(
                        fileList
                          .filter(item => !item.removed)
                          .slice(-2)
                          .reverse(),
                        false
                      )}
                      {fileList?.filter(file => !file.removed).length > 2 && (
                        <Col span={4}>
                          <Tag
                            className="text-label-more mr-1 mt-1"
                            onClick={showMore}
                          >
                            +{fileList.filter(item => !item.removed).length - 2}
                            more
                          </Tag>
                        </Col>
                      )}
                      {renderModal}
                    </Row>
                  </Col>
                )}
              </React.Fragment>
            );
          }}
        </Field>
      </Row>
    </section>
  );
};

export default FormFileUpload;

const FormMultiInputTagsLabels = {
  modal: {
    title: "Uploaded Documents"
  }
};

const FileUploadStyles = css({
  marginRight: "10px", // to align with dropdown arrow icon
  ".ant-upload": {
    float: "right"
  }
});
